import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, CreditCard, DollarSign, CheckCircle, Clock } from 'lucide-react';
import { formatCurrency } from '../../utils/pricingUtils';

export const SubscriptionTypeCards = ({ isAnnual, currentMonthlyCost, currency, symbol, pricingInfo }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div className="bg-white rounded-lg p-4 border border-purple-100">
      <div className="flex items-center gap-2 text-sm text-slate-600 mb-2"><CreditCard className="w-4 h-4" /><span>Payment Cycle</span></div>
      <div className="text-2xl font-bold text-slate-900">{isAnnual ? 'Annual' : 'Pay-As-You-Go'}</div>
      {isAnnual && <div className="text-xs text-emerald-600 font-semibold mt-1">Saving 17% (2 months free!)</div>}
    </div>
    <div className="bg-white rounded-lg p-4 border border-purple-100">
      <div className="flex items-center gap-2 text-sm text-slate-600 mb-2"><DollarSign className="w-4 h-4" /><span>Monthly Cost</span></div>
      <div className="text-2xl font-bold text-slate-900">{currentMonthlyCost ? formatCurrency(currentMonthlyCost, currency, symbol) : 'N/A'}</div>
      {pricingInfo && pricingInfo.investment_count != null && <div className="text-xs text-slate-500 mt-1">{pricingInfo.investment_count} investment{pricingInfo.investment_count !== 1 ? 's' : ''} tracked</div>}
    </div>
  </div>
);

export const BillingInfoSection = ({ isAnnual, endDate, daysRemaining, currentMonthlyCost, currency, symbol }) => (
  <div className="space-y-3">
    {isAnnual && endDate ? (
      <>
        <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-purple-600" /><span className="text-sm font-medium text-slate-700">Subscription Expires</span></div>
          <span className="text-sm font-bold text-slate-900">{endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
        {daysRemaining !== null && (
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-blue-600" /><span className="text-sm font-medium text-slate-700">Days Remaining</span></div>
            <span className="text-sm font-bold text-blue-600">{daysRemaining} days</span>
          </div>
        )}
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-2 mb-2"><CheckCircle className="w-4 h-4 text-amber-600" /><span className="text-sm font-semibold text-amber-900">Price Locked</span></div>
          <p className="text-xs text-slate-600">Your monthly rate of {formatCurrency(currentMonthlyCost, currency, symbol)} is locked for the entire subscription period.</p>
        </div>
      </>
    ) : (
      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center gap-2 mb-2"><Calendar className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-900">Pay-As-You-Go Billing</span></div>
        <p className="text-xs text-slate-600">Your subscription renews automatically each month. Flat price &mdash; the amount is the same regardless of how many investments you track.</p>
      </div>
    )}
  </div>
);

export const PricingBreakdown = ({ pricingInfo, currency, symbol, isMonthly }) => {
  if (!pricingInfo) return null;
  return (
    <div className="border-t border-purple-100 pt-4">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">Current Pricing Details</h4>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-slate-600">Per Investment Rate:</span><span className="font-medium text-slate-900">{formatCurrency(pricingInfo.per_investment_price, currency, symbol)}</span></div>
        <div className="flex justify-between"><span className="text-slate-600">Price Floor:</span><span className="font-medium text-slate-900">{formatCurrency(pricingInfo.floor, currency, symbol)}</span></div>
        <div className="flex justify-between"><span className="text-slate-600">Price Cap:</span><span className="font-medium text-slate-900">{formatCurrency(pricingInfo.cap, currency, symbol)}</span></div>
        <div className="flex justify-between pt-2 border-t border-slate-200"><span className="text-slate-700 font-semibold">Your Current Monthly:</span><span className="font-bold text-purple-600">{formatCurrency(pricingInfo.calculated_amount, currency, symbol)}</span></div>
        {isMonthly && <div className="text-xs text-slate-500 mt-2 italic">Switch to annual and save {formatCurrency(pricingInfo.calculated_amount * 2, currency, symbol)} per year!</div>}
      </div>
    </div>
  );
};
